import { useState } from 'react';
import { useFetch } from '../../../hooks/useFetch';
import { api } from '../../../services/api/apiClient';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { Button } from '../../ui/Button/Button';
import { Card, EmptyState, ErrorState, Skeleton } from '../../ui/Display/Display';
import { DatePicker } from '../../ui/DatePicker/DatePicker';

/** Account statement with running balance. path: /customers/:id/ledger or /vendors/:id/ledger. */
export function LedgerStatement({ path, chargeLabel = 'Invoiced', paymentLabel = 'Paid', emptyMessage }: { path: string; chargeLabel?: string; paymentLabel?: string; emptyMessage?: string }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const { data, loading, error, reload } = useFetch(() => api.get(path, { from, to }).then((r) => r.data), [path, from, to]);

  return (
    <Card className="overflow-hidden">
      <div className="grid gap-3 border-b border-line p-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
        <DatePicker label="From" size="sm" value={from} onChange={(v) => setFrom(v ?? '')} max={to || undefined} />
        <DatePicker label="To" size="sm" value={to} onChange={(v) => setTo(v ?? '')} min={from || undefined} />
        {(from || to) && (
          <Button variant="ghost" size="sm" onClick={() => { setFrom(''); setTo(''); }}>Show all</Button>
        )}
      </div>
      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading || !data ? (
        <div className="space-y-3 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9" />)}</div>
      ) : data.entries.length === 0 ? (
        <EmptyState icon="receipt" title="Nothing in this period" message={emptyMessage} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead className="border-b border-line text-[13px] text-ink-500">
              <tr>
                <th scope="col" className="px-4 py-3 font-normal">Date</th>
                <th scope="col" className="px-4 py-3 font-normal">Details</th>
                <th scope="col" className="px-4 py-3 text-right font-normal">{chargeLabel}</th>
                <th scope="col" className="px-4 py-3 text-right font-normal">{paymentLabel}</th>
                <th scope="col" className="px-4 py-3 text-right font-normal">Balance</th>
              </tr>
            </thead>
            <tbody className="tabular divide-y divide-line">
              <tr className="bg-navy-50/50 text-ink-500">
                <td className="px-4 py-2.5" colSpan={4}>Opening balance</td>
                <td className="px-4 py-2.5 text-right font-medium">{money(data.openingBalance)}</td>
              </tr>
              {data.entries.map((e) => (
                <tr key={`${e.type}-${e.refId}`}>
                  <td className="whitespace-nowrap px-4 py-2.5">{formatDate(e.date)}</td>
                  <td className="px-4 py-2.5 capitalize">{e.description}</td>
                  <td className="px-4 py-2.5 text-right">{e.charge ? money(e.charge) : ''}</td>
                  <td className="px-4 py-2.5 text-right text-emerald-700">{e.payment ? money(e.payment) : ''}</td>
                  <td className="px-4 py-2.5 text-right font-medium">{money(e.balance)}</td>
                </tr>
              ))}
              <tr className="bg-brand-50/70 font-semibold text-navy-900">
                <td className="px-4 py-3" colSpan={2}>Closing balance</td>
                <td className="px-4 py-3 text-right">{money(data.totalCharges)}</td>
                <td className="px-4 py-3 text-right">{money(data.totalPayments)}</td>
                <td className="px-4 py-3 text-right">{money(data.closingBalance)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default LedgerStatement;
